import type { Identifier, XYCoord } from "dnd-core";
import React, { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import styled from "styled-components";

type StyleProps = {
  isDragging: boolean;
  canDrag: boolean;
};

const Draggable = styled.div<StyleProps>`
  &:hover {
    cursor: ${(props) => (props.isDragging ? "grab" : props.canDrag ? "pointer" : "cursor")};
    opacity: ${(props) => (props.isDragging ? 0.5 : 1)};
  }
`;

type DragItem = {
  index: number;
};

type DraggableItemProps = {
  groupName: string;
  canDrag: boolean;
  index: number;
  onDrag: (dragIndex: number, hoverIndex: number) => void;
  onDrop: (dragIndex: number, hoverIndex: number) => void;
  className?: string;
};

const DraggableItem: React.FC<React.PropsWithChildren<DraggableItemProps>> = ({
  groupName,
  canDrag,
  index,
  children,
  onDrag,
  onDrop,
  className = "",
}) => {
  const ref = useRef<HTMLElement>(null);

  const [{ isDragging }, dragRef] = useDrag({
    type: groupName,
    item: { index },
    canDrag: canDrag,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // useDrop - the list item is also a drop area
  const [{ handlerId }, dropRef] = useDrop<DragItem, void, { handlerId: Identifier }>({
    accept: groupName,
    hover: (item, monitor) => {
      const dragIndex = item.index;
      const hoverIndex = index;
      if (!!monitor) {
        const clientOffset: XYCoord | null = monitor.getClientOffset();
        if (!!ref.current && !!clientOffset) {
          const hoverBoundingRect = ref.current?.getBoundingClientRect();
          const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
          const hoverActualY = clientOffset.y - hoverBoundingRect.top;
          // if dragging down, continue only when hover is smaller than middle Y
          if (dragIndex < hoverIndex && hoverActualY < hoverMiddleY) return;
          // if dragging up, continue only when hover is bigger than middle Y
          if (dragIndex > hoverIndex && hoverActualY > hoverMiddleY) return;
        }
      }
      onDrag(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop: (item, _monitor) => {
      const dragIndex = item.index;
      const hoverIndex = index;
      onDrop(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const dragDropRef = dragRef(dropRef(ref));

  return (
    <Draggable
      // @ts-ignore
      ref={dragDropRef}
      className={className}
      canDrag={canDrag}
      isDragging={isDragging}
      data-handler-id={handlerId}
    >
      {children}
    </Draggable>
  );
};

export default DraggableItem;
